import React, { useMemo, useState } from "react";
import { X as XIcon, Copy, Check, Twitter, MessageSquare } from "lucide-react";
import { toast } from "sonner";

export default function ShareModal({ tournament, onClose }) {
  const [copied, setCopied] = useState(false);

  const { shareUrl, ogUrl, tweetUrl, discordText } = useMemo(() => {
    const origin =
      typeof window !== "undefined" ? window.location.origin : "";
    const apiBase =
      process.env.REACT_APP_BACKEND_URL || origin;
    // Public share URL — redirects humans to SPA, gives crawlers OG meta
    const share = `${apiBase}/api/share/tournament/${tournament.id}`;
    const og = `${apiBase}/api/og/tournament/${tournament.id}.png`;
    const text =
      (tournament.status === "live" ? "🔥 LIVE NOW: " : "") +
      tournament.name +
      ` · $${(tournament.prize_pool || 0).toLocaleString()} prize pool`;
    const tweet = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      text
    )}&url=${encodeURIComponent(share)}`;
    const discord = `${text}\n${share}`;
    return { shareUrl: share, ogUrl: og, tweetUrl: tweet, discordText: discord };
  }, [tournament]);

  const copy = async (text, label = "Link") => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${label} copied to clipboard`);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed — please select and copy manually");
    }
  };

  const nativeShare = async () => {
    if (!navigator.share) {
      copy(shareUrl);
      return;
    }
    try {
      await navigator.share({
        title: tournament.name,
        text: `Spicy Community · ${tournament.name}`,
        url: shareUrl,
      });
    } catch {
      /* user cancelled */
    }
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
      data-testid="share-modal-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="share-modal"
        className="glass-red-glow rounded-2xl max-w-lg w-full p-7 relative"
      >
        <button
          onClick={onClose}
          data-testid="share-modal-close"
          className="absolute top-4 right-4 h-8 w-8 rounded-full glass-dark flex items-center justify-center hover:text-red-400"
        >
          <XIcon size={14} />
        </button>

        <div className="text-xs uppercase tracking-[0.3em] text-red-400 font-mono mb-1">
          Broadcast
        </div>
        <h2 className="font-display font-black text-3xl tracking-tight mb-1">
          Share bracket
        </h2>
        <p className="text-sm text-zinc-400 mb-5">
          A read-only public link with a rich preview — perfect for Discord &
          Twitter.
        </p>

        {/* OG image preview */}
        <div
          className="rounded-xl overflow-hidden border border-white/10 mb-5 bg-black"
          data-testid="share-og-preview"
        >
          <img
            src={ogUrl}
            alt="Preview"
            className="w-full h-auto block"
            loading="lazy"
          />
        </div>

        {/* URL copy row */}
        <div className="flex items-stretch gap-2 mb-1">
          <input
            readOnly
            value={shareUrl}
            data-testid="share-url-input"
            onFocus={(e) => e.target.select()}
            className="flex-1 min-w-0 bg-black/60 border border-white/10 rounded-xl px-4 text-sm text-zinc-200 focus:outline-none focus:border-red-500"
          />
          <button
            onClick={() => copy(shareUrl)}
            data-testid="share-copy-url"
            className="btn-liquid !px-4 !text-xs whitespace-nowrap"
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? "Copied" : "Copy link"}
          </button>
        </div>
        <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-3">
          This link serves rich previews to Discord & Twitter — humans get redirected to the bracket automatically.
        </p>

        {/* Action row */}
        <div className="grid grid-cols-3 gap-2">
          <a
            href={tweetUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="share-twitter"
            className="btn-ghost-glass !text-xs !py-2.5 justify-center"
          >
            <Twitter size={14} /> Twitter
          </a>
          <button
            onClick={() => copy(discordText, "Discord snippet")}
            data-testid="share-discord"
            className="btn-ghost-glass !text-xs !py-2.5 justify-center"
          >
            <MessageSquare size={14} /> Discord
          </button>
          <button
            onClick={nativeShare}
            data-testid="share-native"
            className="btn-ghost-glass !text-xs !py-2.5 justify-center"
          >
            More…
          </button>
        </div>
      </div>
    </div>
  );
}
